/*
  bf_turnpurchase.ts
  Copyright (c) 2013-present Human Cube Inc.

  Does all turn calculation, etc.  Must all be synchronous and not involve any external DB or
  file access.  Also does not access any external user data.
  These restrictions are to allow it to be re-used in other environments such as the client.
*/

import BF from '../bfconst1';
import bfH from '../bf_helper';
import { Game, PlayerNormalTurn, PlayerPurchases } from '../gameTypes';
import { confirmOrFixXY } from '../bf_hitmap';
import bfTurnReplay from './bf_turnreplay';


export function doTurnPurchase (game: Game, turns: PlayerNormalTurn[]) {
  for(let playerIndex = 1; playerIndex <= game.players; playerIndex++) {
    if( !game.finished[playerIndex][0] &&  // Player still in the game.
        turns[playerIndex] && turns[playerIndex].purchase &&
        turns[playerIndex].purchase.length > 0 ) {
      doPlayerTurnPurchase(game, turns[playerIndex].purchase, playerIndex);
    }
  }
}

function waterZoneBlockaded (game: Game, zoneID: number, playerIndex: number) {
  // Returns true if the given player is prevented production due to a navy blockade.
  // Forces placed this turn do not affect the status.
  const allyBits = game.fixedAlly[playerIndex];
  let blocked = false;
  for(let p = 0; p <= game.players; p++) {  // [0..players]
    for(let i = 0; i < game.force[p].length; i++) {
      const force = game.force[p][i];
      if(force[BF.FORCE_INDEX_ZONE] !== zoneID ||
          force[BF.FORCE_INDEX_ZONE_PREV] === BF.FORCE_ZONE_PRODUCED) {
        continue;  // Not in this zone or was just purchased this turn. So skip to next one.
      }

      const forceType = force[BF.FORCE_INDEX_TYPE];
      const forceFlags = game.forceFlags[forceType];
      if(!(forceFlags & BF.FORCE_FLAG_BLOCKS_SURFACE)) {
        continue;  // This force type does not affect surface blocking. So skip to next one.
      }

      if(allyBits & (1 << p)) {
        return false;  // Not blocked as player (or ally) has at least one force here.
      }
      blocked = true;  // Blocked only if there are also no friendlies here.
      break;
    }
  }
  return blocked;
}


function doPlayerTurnPurchase (game: Game, turnPurchase: PlayerPurchases, playerIndex: number) {

    // Cap the number of purchases the player may make based on the maxBuy value:
    if(Array.isArray(game.maxBuy))
        if(turnPurchase.length > game.maxBuy[playerIndex])
            turnPurchase.length = game.maxBuy[playerIndex];

    // Sparse array, indexed by zoneID.  Empty values will not have an entry.
    // 0 for no available production
    // positive value for number of forces able to produce there for a land zone.
    // 0 for water zones, they will be determined by neighbouring status.
    let production = [];

    // Sparse array, indexed by zoneID.  Set to 1 if zone can produce different zone type starting
    // forces in an appropriate neighbour.  For example a land factory producing navy in
    // water zone neighbouring:
    let canProduceNeigbour = [];

    let zones = game.zones;

    let len = game.force[playerIndex].length;
    for(let j = 0; j < len; j++) {
        let zoneID = game.force[playerIndex][j][BF.FORCE_INDEX_ZONE];
        if(zoneID > 0 && zoneID <= zones) {
            if(game.zoneOwner[zoneID] === playerIndex &&
                    game.zoneOwnerPrev[zoneID] === playerIndex) {
                let forceType = game.force[playerIndex][j][BF.FORCE_INDEX_TYPE];
                let forceFlags = game.forceFlags[forceType];
                let prod = game.forceProduction[forceType];
                if(!production[zoneID])
                    production[zoneID] = 0;

                if((forceFlags & BF.FORCE_FLAG_CAPITAL_PROD_UNLIMITED) &&
                        bfH.isCapital(game, zoneID))
                    production[zoneID] = 2000000000;   // Unlimited production at any capital.
                else if(forceFlags & BF.FORCE_FLAG_PROD_SCALE_ZONE_INCOME)
                    production[zoneID] += prod * game.zoneIncome[zoneID];
                else production[zoneID] += prod;

                if(forceFlags & BF.FORCE_FLAG_CAN_PRODUCE_NEIGHBOUR)
                    canProduceNeigbour[zoneID] = 1;
            }
        }
    }

    // Validate all purchase force types, canbuy and zoneIDs, regardless of land or water or production.
    // Do land purchases first that require production.  This will help reduce the possibility of
    // utilizing the wrong production for water forces.
    for(let p = 0; p < turnPurchase.length; p++) {
        // [force type index, zone id to place at, x to place at, y to place at]
        let forceType = turnPurchase[p][0];
        if(forceType < 0 || forceType >= game.forceID.length) {
            // invalid forceType
            return;   // Abort all further purchases for this player.
        }

        let zoneID = turnPurchase[p][1];
        if(!zoneID || zoneID < 1 || zoneID > zones) {
            // invalid zoneID.
            return;   // Abort all further purchases for this player.
        }

        if(!(game.canBuy[playerIndex] & (1<<forceType))) {
            // Player is not allowed to buy this force type.
            return;   // Abort all further purchases for this player.
        }

        let x = turnPurchase[p][2];
        let y = turnPurchase[p][3];

        if((game.forceFlags[forceType] & BF.FORCE_FLAG_STARTS_ON_LAND) &&
                (game.forceFlags[forceType] & BF.FORCE_FLAG_REQUIRES_PRODUCTION)) {
            if(bfH.isLand(game, zoneID)) {
                if(production[zoneID] > 0) {
                    if(game.cash[playerIndex] >= game.forceCost[forceType]) {
                        let pos = confirmOrFixXY(game, zoneID, x, y);
                        let forceID = bfH.createNewForce(game, playerIndex, forceType, zoneID,
                                                         BF.FORCE_ZONE_PRODUCED, pos.x, pos.y);
                        bfTurnReplay.purchase(game, forceID);
                        game.cash[playerIndex] -= game.forceCost[forceType];
                        production[zoneID]--;
                    }
                    else {
                        // Cannot afford to buy force.
                        console.log('Cannot afford to buy force.');
                    }
                }
                else {
                    // Not enough production to produce force.
                    console.log('Not enough production to produce force.', playerIndex, forceType, zoneID);
                }
            }
            else {
                // Land force must be built on land.
                console.log('Land force must be built on land.');
            }
        }
    }

    // Do water purchases next that require production.
    for(let p = 0; p < turnPurchase.length; p++) {
        // [force type index, zone id to place at, x to place at, y to place at]
        let forceType = turnPurchase[p][0];
        let zoneID = turnPurchase[p][1];
        let x = turnPurchase[p][2];
        let y = turnPurchase[p][3];

        if((game.forceFlags[forceType] & BF.FORCE_FLAG_STARTS_ON_WATER) &&
                (game.forceFlags[forceType] & BF.FORCE_FLAG_REQUIRES_PRODUCTION)) {
            if(bfH.isWater(game, zoneID)) {
                if(game.cash[playerIndex] >= game.forceCost[forceType]) {
                    if(!waterZoneBlockaded(game, zoneID, playerIndex)) {
                        if(production[zoneID] > 0) {
                            // Production is actually available in the water zone.
                            let pos = confirmOrFixXY(game, zoneID, x, y);
                            let forceID = bfH.createNewForce(game, playerIndex, forceType, zoneID,
                                                             BF.FORCE_ZONE_PRODUCED, pos.x, pos.y);
                            bfTurnReplay.purchase(game, forceID);
                            game.cash[playerIndex] -= game.forceCost[forceType];
                            production[zoneID]--;
                        }
                        else {
                            // Check if there is a neighbouring land based production facility with
                            // neighbour production ability:
                            let madeIt = false;

                            // Try a neighbour with unlimited production first:
                            for(let n = 0; n < game.bordersLand[zoneID].length ; n++) {
                                let z = game.bordersLand[zoneID][n];
                                if(canProduceNeigbour[z] && production[z] &&
                                        production[z] >= 1000000000) {  // Unlimited production.
                                    madeIt = true;
                                    let pos = confirmOrFixXY(game, zoneID, x, y);
                                    let forceID = bfH.createNewForce(game, playerIndex, forceType,
                                                                     zoneID, BF.FORCE_ZONE_PRODUCED,
                                                                     pos.x, pos.y);
                                    bfTurnReplay.purchase(game, forceID);
                                    game.cash[playerIndex] -= game.forceCost[forceType];
                                    production[z]--;
                                    break;
                                }
                            }

                            if(!madeIt) {
                                // Then try a neighbour with any production next:
                                for(let n = 0; n < game.bordersLand[zoneID].length ; n++) {
                                    let z = game.bordersLand[zoneID][n];
                                    if(canProduceNeigbour[z] && production[z] && production[z] > 0) {
                                        madeIt = true;
                                        let pos = confirmOrFixXY(game, zoneID, x, y);
                                        let forceID = bfH.createNewForce(game, playerIndex,
                                                                         forceType, zoneID,
                                                                         BF.FORCE_ZONE_PRODUCED,
                                                                         pos.x, pos.y);
                                        bfTurnReplay.purchase(game, forceID);
                                        game.cash[playerIndex] -= game.forceCost[forceType];
                                        production[z]--;
                                        break;
                                    }
                                }
                            }

                            if( !madeIt ) {
                                // Not enough production to produce force.
                                console.log('Not enough production to produce water force.');
                            }
                        }
                    }
                    else {
                        // The water zone is blockaided.
                        bfTurnReplay.blockaid(game, playerIndex, zoneID, forceType);
                    }
                }
                else {
                    // Cannot afford to buy force.
                    console.log('Cannot afford to buy water force.');
                }
            }
            else {
                // Water force must be built on water.
                console.log('Water force must be built on water.');
            }
        }
    }

    // Finally, do any remaining purchases that do not require a production location:
    for(let p = 0; p < turnPurchase.length; p++) {
        // [force type index, zone id to place at, x to place at, y to place at]
        const forceType = turnPurchase[p][0];
        const zoneID = turnPurchase[p][1];
        const x = turnPurchase[p][2];
        const y = turnPurchase[p][3];

        if(game.forceFlags[forceType] & BF.FORCE_FLAG_REQUIRES_PRODUCTION)
            continue;

        if(game.forceFlags[forceType] & BF.FORCE_FLAG_PROD_MAX_ONE_PER_ZONE) {
            let alreadyThere = false;
            for(let j = 0; j < game.force[playerIndex].length; j++) {
                if(game.force[playerIndex][j][BF.FORCE_INDEX_ZONE] === zoneID &&
                        game.force[playerIndex][j][BF.FORCE_INDEX_TYPE] === forceType) {
                    alreadyThere = true;
                    break;
                }
            }
            if(alreadyThere) {
                // There is already a force of this type there, so prevent the purchase.
                bfTurnReplay.alreadyExists(game, playerIndex, zoneID, forceType);
                continue;
            }
        }

        if(game.forceFlags[forceType] & BF.FORCE_FLAG_STARTS_ON_LAND) {
            if(!bfH.isLand(game, zoneID)) {
                // Land force must be built on land.
                console.log('Land force must be built on land.');
                continue;
            }
            if(game.zoneOwner[zoneID] !== playerIndex ||
                    game.zoneOwnerPrev[zoneID] !== playerIndex) {
                // Player does not or did not previously own the destination location.
                bfTurnReplay.cannotBuild(game, playerIndex, zoneID, forceType);
                continue;
            }
        }

        if(game.forceFlags[forceType] & BF.FORCE_FLAG_STARTS_ON_WATER) {
            if(!bfH.isWater(game, zoneID)) {
                // Water force must be built on water.
                console.log('Water force must be built on water.');
                continue;
            }
            if(waterZoneBlockaded(game, zoneID, playerIndex)) {
                // The water zone is blockaided.
                bfTurnReplay.blockaid(game, playerIndex, zoneID, forceType);
                continue;
            }
        }

        if(game.cash[playerIndex] < game.forceCost[forceType]) {
             // Cannot afford to buy force.
             console.log('Cannot afford to buy force.');
             continue;
        }

        const pos = confirmOrFixXY(game, zoneID, x, y);
        const forceID = bfH.createNewForce(game, playerIndex, forceType, zoneID,
                                         BF.FORCE_ZONE_PRODUCED, pos.x, pos.y);
        bfTurnReplay.purchase(game, forceID);
        game.cash[playerIndex] -= game.forceCost[forceType];
    }
}
